import "./index.scss";
import React, { FC, useCallback, useEffect, useState } from "react";
import { Button, Form, message, Modal, Popconfirm, Table } from "antd";
import { useSelector } from "react-redux";
import store, { RootState } from "../../../../store";
import { ColumnsType } from "antd/es/table";
import { useActions } from "../../../../hooks/useActions";
import { renderNullableText } from "../../../../utils/renderNullableText";
import { I18n } from "react-redux-i18n";
import { SmsProviderType } from "../../../../types/smsProvider";
import { deleteSmsProvider } from "../../../../store/slices/smsProviders/smsProvidersService";
import { FormItems } from "../helpers/formItems";
import FooterActions from "../../../../components/FooterActions";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

const columns = (handleChange: (value: SmsProviderType) => void): ColumnsType<SmsProviderType> => [
  {
    title: I18n.t("connectionName"),
    dataIndex: "description",
    key: "description",
    render: renderNullableText,
  },
  {
    title: I18n.t("provider"),
    dataIndex: "providerType",
    key: "providerType",
    render: renderNullableText,
  },
  {
    title: I18n.t("actions"),
    key: "action",
    render: (_, record) => {
      return (
        <div style={{ display: "flex" }}>
          <Button onClick={() => handleChange(record)} type={"text"} icon={<EditOutlined />}></Button>
          <Popconfirm
            title={I18n.t("deleteEmailProviderConfirmation")}
            onConfirm={() => smsProviderDeleteHandler(record?.providerId)}
          >
            <Button type={"text"} icon={<DeleteOutlined />}></Button>
          </Popconfirm>
        </div>
      );
    },
  },
];

function smsProviderDeleteHandler(id?: number) {
  if (!id) return;
  store.dispatch(deleteSmsProvider(id));
}

const SmsProvidersTableComponent: FC = () => {
  const data = useSelector((state: RootState) => state.smsProviders.list);
  const { getSmsProviders, updateSmsProvider } = useActions();
  const [current, setCurrent] = useState<SmsProviderType | undefined>();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<SmsProviderType>();

  const changeVisible = useCallback(() => {
    setOpen((prev) => !prev);
  }, [setOpen]);

  useEffect(() => {
    getSmsProviders();
  }, []);

  const handleEditClick = useCallback(
    (value: SmsProviderType) => {
      setCurrent(value);
      changeVisible();
      form.setFieldsValue(value);
    },
    [setCurrent, changeVisible],
  );

  const handleCancel = useCallback(() => {
    setCurrent(undefined);
    setOpen(false);
    form.resetFields();
  }, [setCurrent, setOpen, form]);

  const handleSubmit = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        if (current?.providerId) {
          updateSmsProvider({ smsProvidersId: current.providerId, values });
        }
        handleCancel();
      })
      .catch(() => message.error(I18n.t("fillRequiredFields")));
  }, [form, handleCancel, current]);

  return (
    <>
      <Table
        pagination={{
          size: "small",
          showSizeChanger: true,
          defaultPageSize: 10,
        }}
        dataSource={data}
        columns={columns(handleEditClick)}
        rowKey={(record: SmsProviderType) => record?.providerId + ""}
      />
      <Modal
        open={open}
        onCancel={handleCancel}
        title={`${I18n.t("change")} ${current?.providerType}`}
        footer={<FooterActions onCancel={handleCancel} onSubmit={handleSubmit} submitLabelTranslationKey={"save"} />}
      >
        <Form form={form} layout={"vertical"} style={{ marginTop: 20 }}>
          <FormItems />
        </Form>
      </Modal>
    </>
  );
};

export default SmsProvidersTableComponent;
